'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

const CHUNK_RELOAD_KEY = '__chunk_reload_attempted__';

function isChunkLoadError(error: Error & { name?: string }): boolean {
  const msg = error?.message ?? '';
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg)
  );
}

// TG WebView (особенно iOS) и edge-кэш CF могут игнорировать Cache-Control
// и отдавать старый HTML со ссылкой на удалённые chunks. location.reload()
// это не пробивает — нужен уникальный URL через query string.
function hardReloadWithCacheBust() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('_cb', String(Date.now()));
  window.location.replace(url.toString());
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('GlobalError caught:', error);

    if (isChunkLoadError(error) && typeof window !== 'undefined') {
      const alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyTried) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        hardReloadWithCacheBust();
        return;
      }
    }

    Sentry.captureException(error, {
      tags: { digest: error.digest ?? 'no-digest' },
    });
  }, [error]);

  function handleRetry() {
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      hardReloadWithCacheBust();
      return;
    }
    reset();
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#06060A',
        color: '#fff',
        padding: 24,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1 style={{ color: '#FF2E63', marginBottom: 16 }}>Что-то сломалось</h1>
      <p style={{ marginBottom: 12 }}>{error.message}</p>
      {error.digest && (
        <p style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
          digest: {error.digest}
        </p>
      )}
      <pre
        style={{
          fontSize: 11,
          background: '#1a1a1a',
          padding: 12,
          overflow: 'auto',
          maxHeight: '60vh',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {error.stack}
      </pre>
      <button
        onClick={handleRetry}
        style={{
          marginTop: 16,
          padding: '12px 24px',
          background: '#C6FF3D',
          color: '#06060A',
          border: 'none',
          fontWeight: 700,
        }}
      >
        Попробовать снова
      </button>
    </div>
  );
}
